import * as THREE from 'three';
import avatarLipsyncConfig from '../config/avatarLipsyncConfig';
import {
  ARKIT52_BLENDSHAPES,
  VISEME15_BLENDSHAPES,
  VISEME15_CONSONANTS,
  VISEME15_OPEN_VOWELS,
  VISEME15_OPENNESS
} from '../constants/viseme15Blendshapes';
import { lerpMorphTarget } from './utils';

const { frameRate, viseme15: viseme15Config } = avatarLipsyncConfig;

export const NUDGYT_AGENT_FPS = frameRate.nudgytAgent;
export const CONVAI_FPS = frameRate.convai;

export function getFacialFrameRate(client) {
  return client?.isNudgytAgentClient ? NUDGYT_AGENT_FPS : CONVAI_FPS;
}

export function getInterpolatedFrameIndex(facialData, audioCurrentTime, fps) {
  const exactFrame = audioCurrentTime * fps;
  const prevFrameIndex = Math.floor(exactFrame);
  const nextFrameIndex = Math.ceil(exactFrame);
  const lerpFactor = exactFrame - prevFrameIndex;

  return {
    prevFrameIndex,
    nextFrameIndex,
    lerpFactor,
    prevFrame: facialData[prevFrameIndex],
    nextFrame: facialData[nextFrameIndex]
  };
}

function interpolateScalar(prevVal, nextVal, lerpFactor) {
  if (prevVal == null || nextVal == null) return null;
  return prevVal + (nextVal - prevVal) * lerpFactor;
}

export function interpolateViseme15Frame(prevFrame, nextFrame, lerpFactor) {
  if (prevFrame == null || nextFrame == null) return null;

  if (Array.isArray(prevFrame) && Array.isArray(nextFrame)) {
    const length = Math.min(prevFrame.length, nextFrame.length);
    const blendShapeMap =
      length >= ARKIT52_BLENDSHAPES.length
        ? ARKIT52_BLENDSHAPES
        : VISEME15_BLENDSHAPES;
    const entries = [];

    for (let idx = 0; idx < length; idx += 1) {
      const blendName = Array.isArray(blendShapeMap)
        ? blendShapeMap[idx]
        : blendShapeMap[idx];
      const value = interpolateScalar(
        prevFrame[idx],
        nextFrame[idx],
        lerpFactor
      );

      if (blendName && value != null) {
        entries.push([blendName, value]);
      }
    }

    return entries;
  }

  if (
    typeof prevFrame === 'object' &&
    typeof nextFrame === 'object' &&
    !Array.isArray(prevFrame) &&
    !Array.isArray(nextFrame)
  ) {
    const keys = new Set([
      ...Object.keys(prevFrame),
      ...Object.keys(nextFrame)
    ]);
    const entries = [];

    keys.forEach((key) => {
      const value = interpolateScalar(
        prevFrame[key],
        nextFrame[key],
        lerpFactor
      );

      if (value != null) {
        entries.push([key, value]);
      }
    });

    return entries;
  }

  return null;
}

/** Attenuates silence, boosts open vowels, and trims consonants when vowels dominate. */
export function refineViseme15Entries(entries) {
  if (!entries?.length) return [];

  const weights = Object.fromEntries(entries);
  let maxSpeech = 0;

  Object.entries(weights).forEach(([name, value]) => {
    if (name !== 'viseme_sil') {
      maxSpeech = Math.max(maxSpeech, value);
    }
  });

  if (maxSpeech > 0.06 && weights.viseme_sil != null) {
    const silScale = Math.max(0, 1 - maxSpeech * 2.2);
    weights.viseme_sil *= silScale;
  }

  VISEME15_OPEN_VOWELS.forEach((name) => {
    const value = weights[name];
    if (value == null || value <= 0.04) return;

    const boost = viseme15Config.vowelBoost[name] ?? 1;
    const ceiling = viseme15Config.maxWeight[name] ?? 1;

    weights[name] = Math.min(ceiling, value * boost);
  });

  const maxVowel = Math.max(
    ...VISEME15_OPEN_VOWELS.map((name) => weights[name] ?? 0)
  );

  if (maxVowel > 0.18) {
    VISEME15_CONSONANTS.forEach((name) => {
      const value = weights[name];
      if (value != null && value < maxVowel * 0.55) {
        weights[name] = value * 0.35;
      }
    });
  }

  return Object.entries(weights).filter(
    ([name, value]) =>
      name.startsWith('viseme_') && value > viseme15Config.minWeight
  );
}

/** Weighted-average mouth openness derived from active viseme weights. */
export function computeMouthOpenness(entries) {
  if (!entries?.length) return 0;

  let weightedSum = 0;
  let totalWeight = 0;

  entries.forEach(([name, value]) => {
    const openness = VISEME15_OPENNESS[name];
    if (openness == null || !(value > 0)) return;

    weightedSum += openness * value;
    totalWeight += value;
  });

  if (totalWeight <= 0) return 0;

  const average = weightedSum / totalWeight;
  const speechEnergy = Math.min(
    1,
    totalWeight / viseme15Config.fullEnergyWeight
  );
  const shaped = Math.pow(average, viseme15Config.mouthCloseContrast);

  return Math.min(1, shaped * speechEnergy * viseme15Config.mouthOpenGain);
}

export function smoothVisemeEntries(
  entries,
  previousWeights,
  alpha = viseme15Config.smoothAlpha
) {
  const nextWeights = { ...previousWeights };
  const touched = new Set();

  entries.forEach(([name, value]) => {
    touched.add(name);
    const prev = previousWeights[name] ?? 0;
    nextWeights[name] = prev + (value - prev) * alpha;
  });

  Object.keys(previousWeights).forEach((name) => {
    if (touched.has(name)) return;
    nextWeights[name] = (previousWeights[name] ?? 0) * (1 - alpha);
    if (nextWeights[name] <= viseme15Config.minWeight) {
      delete nextWeights[name];
    }
  });

  return {
    entries: Object.entries(nextWeights).filter(
      ([, value]) => value > viseme15Config.minWeight
    ),
    nextWeights
  };
}

export function applyVisemeBlendshape({ targetBlendShape, value, speed, scene }) {
  lerpMorphTarget({ targetBlendShape, value, speed, scene });
}

export function resetMorphTargets(
  scene,
  targetNames,
  { preserveNames = [], lerpSpeed = 1 } = {}
) {
  const preserve = new Set(preserveNames);

  scene.traverse((child) => {
    if (!child.isSkinnedMesh || !child.morphTargetDictionary) return;

    targetNames.forEach((target) => {
      if (preserve.has(target)) return;

      const index = child.morphTargetDictionary[target];
      if (
        index === undefined ||
        child.morphTargetInfluences?.[index] === undefined
      ) {
        return;
      }

      child.morphTargetInfluences[index] = THREE.MathUtils.lerp(
        child.morphTargetInfluences[index],
        0,
        lerpSpeed
      );
    });
  });
}
